import { useState, useEffect, useRef, useCallback } from 'react';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, query, orderBy, setDoc, Timestamp, getDocs,
} from 'firebase/firestore';
import { ref, getDownloadURL, uploadBytes } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { QRCodeSVG } from 'qrcode.react';
import JSZip from 'jszip';
import { db, storage, functions } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getCurrentWeekRates, toGBP, getRateForCurrency, getWeekStart } from '../../utils/exchangeRates';
import { useExpenseCategories } from '../../utils/expenseCategories';
import InvoiceUpload from '../../components/InvoiceUpload';

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };

const EMPTY_FORM = {
  date: new Date().toISOString().split('T')[0],
  category: 'Accommodation',
  description: '',
  amount: '',
  currency: 'GBP',
  requestId: '',
  requestSummary: '',
  receiptUrl: '',
  paymentStatus: 'paid',
};

function fmt(n, currency = 'GBP') {
  const sym = CURRENCY_SYMBOLS[currency] || '£';
  return `${sym}${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

function parseLocalDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// ── QR Scan Panel ─────────────────────────────────────────────────────────────

const SCAN_URL_BASE = 'https://mst-sports-tours.web.app/scan';
const SESSION_TTL_MS = 10 * 60 * 1000; // 10 minutes

function QRScanPanel({ currentUser, onScanned, onCancel }) {
  const [token,     setToken]     = useState(null);
  const [qrUrl,     setQrUrl]     = useState(null);
  const [phase,     setPhase]     = useState('generating'); // generating | waiting | received | scanning | error
  const [secsLeft,  setSecsLeft]  = useState(600);
  const unsubRef   = useRef(null);
  const timerRef   = useRef(null);

  const teardown = useCallback(() => {
    unsubRef.current?.();
    clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    async function createSession() {
      const t = crypto.randomUUID();
      await setDoc(doc(db, 'scanSessions', t), {
        status:    'pending',
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromDate(new Date(Date.now() + SESSION_TTL_MS)),
      });
      setToken(t);
      setQrUrl(`${SCAN_URL_BASE}/${t}`);
      setPhase('waiting');

      // Countdown timer
      timerRef.current = setInterval(() => {
        setSecsLeft((s) => {
          if (s <= 1) { clearInterval(timerRef.current); setPhase('error'); return 0; }
          return s - 1;
        });
      }, 1000);

      // Listen for phone upload
      unsubRef.current = onSnapshot(doc(db, 'scanSessions', t), async (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        if (data.status !== 'uploaded') return;

        teardown();
        setPhase('scanning');

        try {
          // Get download URL for the thumbnail — best effort, not critical
          let downloadUrl = null;
          try {
            const storageRef = ref(storage, data.storagePath);
            downloadUrl = await getDownloadURL(storageRef);
          } catch (urlErr) {
            console.warn('Could not get receipt thumbnail URL:', urlErr);
          }

          // Cloud Function downloads the image server-side — no CORS
          const scanFn = httpsCallable(functions, 'scanReceipt');
          const result = await scanFn({ storagePath: data.storagePath });
          onScanned(result.data, downloadUrl);
        } catch (err) {
          console.error('Auto-scan error:', err);
          setPhase('error');
        }
      });
    }

    createSession().catch(() => setPhase('error'));
    return teardown;
  }, [currentUser.uid, onScanned, teardown]);

  const mins = String(Math.floor(secsLeft / 60)).padStart(2, '0');
  const secs = String(secsLeft % 60).padStart(2, '0');

  return (
    <div className="p-5 bg-surface rounded-[10px] border border-[#E5E7EB] mb-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-[14px] font-semibold text-[#111827]">Scan Receipt with Phone</h3>
          <p className="text-[12px] text-[#6B7280] mt-0.5">
            Point your phone camera at the QR code — no login needed.
          </p>
        </div>
        <button onClick={() => { teardown(); onCancel(); }} className="text-[12px] text-[#6B7280] hover:text-[#374151] ml-4 flex-shrink-0">
          Cancel
        </button>
      </div>

      {/* Generating */}
      {phase === 'generating' && (
        <div className="flex items-center justify-center py-12">
          <svg className="w-6 h-6 animate-spin text-[#9CA3AF]" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        </div>
      )}

      {/* Waiting for phone */}
      {phase === 'waiting' && qrUrl && (
        <div className="flex flex-col sm:flex-row items-center gap-8">
          {/* QR code */}
          <div className="p-3 bg-white border border-[#E5E7EB] rounded-[12px] flex-shrink-0">
            <QRCodeSVG value={qrUrl} size={160} level="M" />
          </div>
          {/* Instructions */}
          <div className="flex-1">
            <ol className="space-y-3">
              {[
                'Open your phone camera and point it at the QR code',
                'Tap the link that appears — it opens in your browser',
                'Take a photo of the receipt or choose one from your photos',
                'Tap "Send to laptop" — the form fills in automatically here',
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-pb text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-[13px] text-[#374151]">{step}</span>
                </li>
              ))}
            </ol>
            <div className="mt-4 flex items-center gap-2">
              <svg className="w-4 h-4 animate-spin text-[#9CA3AF]" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <span className="text-[12px] text-[#9CA3AF]">Waiting for phone… expires in {mins}:{secs}</span>
            </div>
          </div>
        </div>
      )}

      {/* Photo received — scanning with AI */}
      {phase === 'scanning' && (
        <div className="flex items-center gap-3 py-6">
          <svg className="w-5 h-5 animate-spin text-pb flex-shrink-0" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          <span className="text-[13px] text-[#374151]">Receipt received — scanning with AI…</span>
        </div>
      )}

      {/* Error / timeout */}
      {phase === 'error' && (
        <div className="py-4">
          <p className="text-[13px] text-dr mb-2">
            {secsLeft === 0 ? 'QR code expired.' : 'Something went wrong scanning the receipt.'}
          </p>
          <button
            onClick={() => { teardown(); onCancel(); }}
            className="text-[13px] text-pb hover:text-navy font-medium"
          >
            Close and try again
          </button>
        </div>
      )}
    </div>
  );
}

// ── Mobile Direct Scan Panel ──────────────────────────────────────────────────

function MobileDirectScanPanel({ currentUser, onScanned, onCancel }) {
  const [phase, setPhase] = useState('idle'); // idle | uploading | scanning | error
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef(null);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    setPhase('uploading');
    try {
      const storagePath = `receipts/${currentUser.uid}/direct-${Date.now()}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);

      let downloadUrl = null;
      try { downloadUrl = await getDownloadURL(storageRef); } catch {}

      setPhase('scanning');

      const scanFn = httpsCallable(functions, 'scanReceipt');
      const result = await scanFn({ storagePath });
      onScanned(result.data, downloadUrl);
    } catch (err) {
      console.error('Mobile scan error:', err);
      setErrorMsg(err?.message || 'Something went wrong.');
      setPhase('error');
    }
  }

  return (
    <div className="p-5 bg-surface rounded-[10px] border border-[#E5E7EB] mb-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-[14px] font-semibold text-[#111827]">Scan Receipt</h3>
          <p className="text-[12px] text-[#6B7280] mt-0.5">Take a photo or choose from your camera roll.</p>
        </div>
        <button onClick={onCancel} className="text-[12px] text-[#6B7280] hover:text-[#374151] ml-4 flex-shrink-0">
          Cancel
        </button>
      </div>

      {phase === 'idle' && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleFile}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full py-10 border-2 border-dashed border-[#D1D5DB] rounded-[10px] flex flex-col items-center gap-3 hover:border-pb hover:bg-blue-light/30 active:bg-blue-light/50 transition-colors"
          >
            <svg className="w-10 h-10 text-[#9CA3AF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <div className="text-center">
              <p className="text-[14px] font-semibold text-[#374151]">Take a photo or choose from gallery</p>
              <p className="text-[12px] text-[#9CA3AF] mt-0.5">AI will read the receipt and fill in the form</p>
            </div>
          </button>
        </>
      )}

      {(phase === 'uploading' || phase === 'scanning') && (
        <div className="flex items-center gap-3 py-10 justify-center">
          <svg className="w-5 h-5 animate-spin text-pb flex-shrink-0" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          <span className="text-[13px] text-[#374151]">
            {phase === 'uploading' ? 'Uploading photo…' : 'Scanning with AI…'}
          </span>
        </div>
      )}

      {phase === 'error' && (
        <div className="py-4">
          <p className="text-[13px] text-dr mb-3">{errorMsg}</p>
          <div className="flex gap-3">
            <button
              onClick={() => { setPhase('idle'); setErrorMsg(''); if (fileInputRef.current) fileInputRef.current.value = ''; }}
              className="text-[13px] text-pb hover:text-navy font-medium"
            >
              Try again
            </button>
            <button onClick={onCancel} className="text-[13px] text-[#6B7280] hover:text-[#374151]">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Expense Form ──────────────────────────────────────────────────────────────

function ExpenseForm({ initial = EMPTY_FORM, requests, onSave, onCancel, saving, saveError, receiptPreview }) {
  const expenseCategories = useExpenseCategories();
  const [form, setForm] = useState(initial);

  function set(key, val) {
    setForm((p) => ({ ...p, [key]: val }));
  }

  function handleRequestChange(e) {
    const val = e.target.value;
    if (!val) { set('requestId', ''); set('requestSummary', ''); return; }
    if (val === 'misc') { set('requestId', 'misc'); set('requestSummary', 'MISC'); return; }
    const req = requests.find((r) => r.id === val);
    const sportDest = req ? `${req.sport || ''}${req.destination ? ` — ${req.destination}` : ''}` : '';
    set('requestId', val);
    set('requestSummary', req ? (req.internalRef || sportDest) : '');
  }

  function handleSubmit(e) {
    e.preventDefault();
    if ((!form.storedOnly && !form.amount) || !form.date || !form.category) return;
    onSave(form);
  }

  return (
    <form onSubmit={handleSubmit} className="p-5 bg-surface rounded-[10px] border border-[#E5E7EB]">

      {/* Receipt preview banner (shown when a receipt was scanned) */}
      {receiptPreview && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-[8px]">
          <img src={receiptPreview} alt="Receipt" className="w-10 h-10 object-cover rounded-[4px] border border-emerald-200 flex-shrink-0" />
          <div>
            <p className="text-[12px] font-semibold text-emerald-700">Receipt scanned successfully</p>
            <p className="text-[11px] text-emerald-600">Details pre-filled below — check and correct if needed, then save.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">

        {/* Date */}
        <div>
          <label className="block text-[12px] font-medium text-[#6B7280] mb-1">
            Date <span className="text-dr">*</span>
          </label>
          <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} required
            className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
        </div>

        {/* Category */}
        <div>
          <label className="block text-[12px] font-medium text-[#6B7280] mb-1">
            Category <span className="text-dr">*</span>
          </label>
          <select value={form.category} onChange={(e) => set('category', e.target.value)} required
            className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb bg-white">
            {expenseCategories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Amount + Currency */}
        {!form.storedOnly && <div>
          <label className="block text-[12px] font-medium text-[#6B7280] mb-1">
            Amount <span className="text-dr">*</span>
          </label>
          <div className="flex gap-2">
            <select value={form.currency} onChange={(e) => set('currency', e.target.value)}
              className="w-20 border border-[#E5E7EB] rounded-[8px] px-2 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb bg-white">
              {Object.keys(CURRENCY_SYMBOLS).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="number" step="0.01" min="0" value={form.amount}
              onChange={(e) => set('amount', e.target.value)} required placeholder="0.00"
              className="flex-1 border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
          </div>
        </div>}

        {/* Description */}
        <div className="sm:col-span-2">
          <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Description</label>
          <input type="text" value={form.description} onChange={(e) => set('description', e.target.value)}
            placeholder="e.g. Hotel — Alicante 3 nights"
            className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
        </div>

        {/* Linked request */}
        <div>
          <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Linked Tour</label>
          <select value={form.requestId || ''} onChange={handleRequestChange}
            className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb bg-white">
            <option value="">— Select tour or MISC —</option>
            <option value="misc">MISC (not tour-specific)</option>
            {requests
              .sort((a, b) => (a.departureDate || '').localeCompare(b.departureDate || ''))
              .map((r) => (
                <option key={r.id} value={r.id}>
                  {r.internalRef || `${r.sport || '?'}${r.destination ? ` — ${r.destination}` : ''}${r.departureDate ? ` (${r.departureDate})` : ''}`}
                </option>
              ))}
          </select>
        </div>

        {/* Payment status */}
        {!form.storedOnly && <div>
          <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Payment Status</label>
          <div className="flex rounded-[8px] border border-[#E5E7EB] overflow-hidden w-fit">
            <button type="button"
              onClick={() => set('paymentStatus', 'paid')}
              className={`px-4 py-2 text-[13px] font-semibold transition ${
                form.paymentStatus === 'paid'
                  ? 'bg-emerald-500 text-white'
                  : 'bg-white text-[#6B7280] hover:text-[#374151]'
              }`}>
              Paid
            </button>
            <div className="w-px bg-[#E5E7EB]" />
            <button type="button"
              onClick={() => set('paymentStatus', 'to_pay')}
              className={`px-4 py-2 text-[13px] font-semibold transition ${
                form.paymentStatus === 'to_pay'
                  ? 'bg-amber-400 text-white'
                  : 'bg-white text-[#6B7280] hover:text-[#374151]'
              }`}>
              To Pay
            </button>
          </div>
        </div>}
      </div>

      {saveError && (
        <p className="text-[12px] text-dr mb-2">{saveError}</p>
      )}
      <div className="flex gap-2 pt-1">
        <button type="submit" disabled={saving || (!form.storedOnly && !form.amount) || !form.date}
          className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
          {saving ? 'Saving…' : 'Save expense'}
        </button>
        <button type="button" onClick={onCancel}
          className="px-4 py-2 text-[13px] text-[#6B7280] hover:text-[#374151]">
          Cancel
        </button>
      </div>
    </form>
  );
}

// ── Export Panel ──────────────────────────────────────────────────────────────

// Given an expense date string, find the best matching rates from the map
// { weekStart: { EUR_GBP, USD_GBP } }. Returns { rates, week }.
function resolveRates(dateStr, ratesMap) {
  const weekStart = getWeekStart(dateStr ? new Date(dateStr) : new Date());
  if (ratesMap[weekStart]) return { rates: ratesMap[weekStart], week: weekStart };
  const sorted = Object.keys(ratesMap).sort((a, b) => b.localeCompare(a));
  const closest = sorted.find((w) => w <= weekStart) || sorted[0];
  return { rates: ratesMap[closest] || {}, week: closest || '' };
}

function ExportPanel({ allExpenses, requests, onClose }) {
  const [filterTour,     setFilterTour]     = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo,   setFilterDateTo]   = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterCurrency, setFilterCurrency] = useState('all');
  const [filterStatus,   setFilterStatus]   = useState('all');
  const [csvBusy,        setCsvBusy]        = useState(false);
  const [zipBusy,        setZipBusy]        = useState(false);
  const [zipDone,        setZipDone]        = useState(null);
  const [ratesMap,       setRatesMap]       = useState({});

  useEffect(() => {
    getDocs(collection(db, 'exchangeRates')).then((snap) => {
      const map = {};
      snap.docs.forEach((d) => { map[d.id] = d.data(); });
      setRatesMap(map);
    }).catch(console.error);
  }, []);

  const filtered = allExpenses.filter((e) => {
    if (filterTour !== 'all') {
      if (filterTour === 'misc' ? e.requestId !== 'misc' : e.requestId !== filterTour) return false;
    }
    if (filterDateFrom && e.date < filterDateFrom) return false;
    if (filterDateTo   && e.date > filterDateTo)   return false;
    if (filterCategory !== 'all' && e.category !== filterCategory) return false;
    if (filterCurrency !== 'all' && (e.currency || 'GBP') !== filterCurrency) return false;
    if (filterStatus   !== 'all' && (e.paymentStatus || 'paid') !== filterStatus) return false;
    return true;
  });

  const receiptCount = filtered.filter((e) => e.receiptUrl).length;

  function handleCSV() {
    setCsvBusy(true);
    try {
      const headers = [
        'Date', 'Category', 'Description', 'Linked Tour',
        'Amount', 'Currency', 'GBP Equivalent', 'Exchange Rate', 'Rate Week',
        'Payment Status',
      ];
      const rows = filtered.map((e) => {
        const req      = requests.find((r) => r.id === e.requestId);
        const tour     = e.requestId === 'misc'
          ? 'MISC'
          : req
            ? (req.internalRef || `${req.sport || '?'} — ${req.destination || '?'}`)
            : (e.requestSummary || '—');
        if (e.storedOnly) {
          return [e.date || '', e.category || 'Other', e.description || '', tour, '', '', '', '', '', 'stored'];
        }
        const currency = e.currency || 'GBP';
        const amount   = Number(e.amount || 0);
        const { rates, week } = resolveRates(e.date, ratesMap);
        const gbpEquiv = e.gbpEquivalent != null
          ? Number(e.gbpEquivalent)
          : toGBP(amount, currency, rates);
        const rate     = e.exchangeRateUsed != null
          ? Number(e.exchangeRateUsed)
          : getRateForCurrency(currency, rates);
        const rateWeek = e.rateWeek || week;
        return [
          e.date || '',
          e.category || 'Other',
          e.description || '',
          tour,
          amount.toFixed(2),
          currency,
          gbpEquiv.toFixed(2),
          rate.toFixed(6),
          rateWeek,
          e.paymentStatus || 'paid',
        ];
      });
      const csv = [headers, ...rows]
        .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `mst-expenses-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setCsvBusy(false);
    }
  }

  async function handleZip() {
    setZipBusy(true);
    setZipDone(null);
    try {
      const zip          = new JSZip();
      const withReceipts = filtered.filter((e) => e.receiptUrl);
      let count = 0;
      for (const exp of withReceipts) {
        try {
          const resp = await fetch(exp.receiptUrl);
          if (!resp.ok) continue;
          const blob = await resp.blob();
          const ext  = blob.type.includes('pdf') ? 'pdf' : blob.type.includes('png') ? 'png' : blob.type.includes('gif') ? 'gif' : 'jpg';
          const safe = (exp.description || 'receipt')
            .replace(/[^a-zA-Z0-9 _\-]/g, '').trim().slice(0, 40) || 'receipt';
          zip.file(`${exp.date || 'unknown'}_${safe}.${ext}`, blob);
          count++;
        } catch (err) {
          console.warn('Skipping receipt:', err);
        }
      }
      if (count > 0) {
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const url     = URL.createObjectURL(zipBlob);
        const a       = document.createElement('a');
        a.href        = url;
        a.download    = `mst-receipts-${new Date().toISOString().slice(0, 10)}.zip`;
        a.click();
        URL.revokeObjectURL(url);
      }
      setZipDone(count);
    } catch (err) {
      console.error('ZIP error:', err);
      setZipDone(0);
    } finally {
      setZipBusy(false);
    }
  }

  const tourOptions = [...new Map(
    allExpenses
      .filter((e) => e.requestId && e.requestId !== 'misc')
      .map((e) => {
        const req   = requests.find((r) => r.id === e.requestId);
        const label = req
          ? (req.internalRef || `${req.sport || '?'} — ${req.destination || '?'}${req.departureDate ? ` (${req.departureDate})` : ''}`)
          : (e.requestSummary || e.requestId);
        return [e.requestId, label];
      })
  ).entries()].map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const hasMisc         = allExpenses.some((e) => e.requestId === 'misc');
  const usedCategories  = [...new Set(allExpenses.map((e) => e.category || 'Other'))].sort();
  const usedCurrencies  = [...new Set(allExpenses.map((e) => e.currency || 'GBP'))];

  return (
    <div className="p-5 bg-surface rounded-[10px] border border-[#E5E7EB] mb-6">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h3 className="text-[14px] font-semibold text-[#111827]">Export Expenses</h3>
          <p className="text-[12px] text-[#6B7280] mt-0.5">Apply filters then download as CSV or download all receipt images as a ZIP.</p>
        </div>
        <button onClick={onClose} className="text-[12px] text-[#6B7280] hover:text-[#374151] ml-4 flex-shrink-0">
          Close
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
        <div>
          <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Tour</label>
          <select value={filterTour} onChange={(e) => setFilterTour(e.target.value)}
            className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white">
            <option value="all">All tours</option>
            {hasMisc && <option value="misc">MISC (no tour)</option>}
            {tourOptions.map(({ id, label }) => (
              <option key={id} value={id}>{label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Date from</label>
          <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)}
            className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
        </div>

        <div>
          <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Date to</label>
          <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)}
            className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
        </div>

        <div>
          <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Category</label>
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
            className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white">
            <option value="all">All categories</option>
            {usedCategories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {usedCurrencies.length > 1 && (
          <div>
            <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Currency</label>
            <select value={filterCurrency} onChange={(e) => setFilterCurrency(e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white">
              <option value="all">All currencies</option>
              {usedCurrencies.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}

        <div>
          <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Payment Status</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white">
            <option value="all">All</option>
            <option value="paid">Paid</option>
            <option value="to_pay">To Pay</option>
            <option value="stored">Stored</option>
          </select>
        </div>
      </div>

      {/* Match summary */}
      <p className="text-[12px] text-[#6B7280] mb-4">
        <span className="font-semibold text-[#111827]">{filtered.length}</span> expense{filtered.length !== 1 ? 's' : ''} match your filters
        {receiptCount > 0 && (
          <> · <span className="font-semibold text-[#111827]">{receiptCount}</span> with receipts or invoices</>
        )}
      </p>

      {/* Download buttons */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={handleCSV}
          disabled={csvBusy || filtered.length === 0}
          className="flex items-center gap-2 px-[18px] py-[9px] bg-pb hover:bg-navy text-white text-[13px] font-semibold rounded-[8px] transition disabled:opacity-60"
        >
          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          </svg>
          {csvBusy ? 'Preparing…' : `Download CSV (${filtered.length})`}
        </button>

        <button
          onClick={handleZip}
          disabled={zipBusy || receiptCount === 0}
          className="flex items-center gap-2 px-[18px] py-[9px] border border-[#E5E7EB] hover:border-pb/40 text-[#374151] hover:text-pb text-[13px] font-semibold rounded-[8px] transition disabled:opacity-60"
        >
          {zipBusy ? (
            <>
              <svg className="w-4 h-4 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Downloading receipts…
            </>
          ) : (
            <>
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download Receipts{receiptCount > 0 ? ` (${receiptCount})` : ''}
            </>
          )}
        </button>
      </div>

      {zipDone !== null && (
        <p className={`text-[12px] mt-3 ${zipDone === 0 ? 'text-dr' : 'text-emerald-600'}`}>
          {zipDone === 0
            ? 'No receipts could be downloaded — they may have expired or be unavailable.'
            : `${zipDone} receipt${zipDone !== 1 ? 's' : ''} saved to ZIP.`}
        </p>
      )}
    </div>
  );
}

// ── Main Expenses page ────────────────────────────────────────────────────────

export default function Expenses() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const expenseCategories = useExpenseCategories();

  const [expenses,  setExpenses]  = useState([]);
  const [requests,  setRequests]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [formInitial, setFormInitial] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Scan state
  const [scanMode,       setScanMode]       = useState(false);
  const [showInvoiceUpload, setShowInvoiceUpload] = useState(false);
  const [receiptPreview, setReceiptPreview]  = useState(null); // download URL from Storage

  // Detect mobile so we show direct camera upload instead of QR code
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const [filterCategory,  setFilterCategory]  = useState('all');
  const [filterCurrency,  setFilterCurrency]  = useState('all');
  const [filterTour,      setFilterTour]      = useState('all');
  const [filterDateFrom,  setFilterDateFrom]  = useState('');
  const [filterDateTo,    setFilterDateTo]    = useState('');
  const [filterStatus,    setFilterStatus]    = useState('all');
  const [periodYear, setPeriodYear] = useState(new Date().getFullYear());
  const [showExport, setShowExport] = useState(false);

  useEffect(() => {
    const u1 = onSnapshot(
      query(collection(db, 'expenses'), orderBy('date', 'desc')),
      (snap) => { setExpenses(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoading(false); }
    );
    const u2 = onSnapshot(collection(db, 'requests'), (snap) => {
      setRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => { u1(); u2(); };
  }, []);

  function handleScanned(data, downloadUrl) {
    const form = {
      date:           data.date || new Date().toISOString().split('T')[0],
      category:       expenseCategories.includes(data.category) ? data.category : 'Other',
      description:    data.description || '',
      amount:         data.amount != null ? String(data.amount) : '',
      currency:       ['GBP', 'EUR', 'USD'].includes(data.currency) ? data.currency : 'GBP',
      requestId:      '',
      requestSummary: '',
      receiptUrl:     downloadUrl || '',
    };
    setReceiptPreview(downloadUrl || null);
    setFormInitial(form);
    setScanMode(false);
    setShowForm(true);
  }

  function cancelForm() {
    setShowForm(false);
    setFormInitial(null);
    setReceiptPreview(null);
  }

  async function handleSave(form) {
    setSaving(true);
    setSaveError(null);
    try {
      const rates = await getCurrentWeekRates();
      const currency = form.currency || 'GBP';
      const amount = Number(form.amount);
      await addDoc(collection(db, 'expenses'), {
        ...form,
        amount,
        brand: 'mst',
        gbpEquivalent: toGBP(amount, currency, rates),
        exchangeRateUsed: getRateForCurrency(currency, rates),
        rateWeek: rates.weekStart,
        createdAt: serverTimestamp(),
        createdBy: currentUser.email,
      });
      // Jump to the year of the saved expense so it's immediately visible
      if (form.date) {
        const savedYear = new Date(form.date).getFullYear();
        if (!isNaN(savedYear)) setPeriodYear(savedYear);
      }
      cancelForm();
    } catch (err) {
      console.error('Save expense error:', err);
      setSaveError(err?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit(id, form) {
    setSaving(true);
    try {
      if (form.storedOnly) {
        await updateDoc(doc(db, 'expenses', id), {
          date:           form.date,
          category:       form.category,
          description:    form.description,
          requestId:      form.requestId,
          requestSummary: form.requestSummary,
          updatedAt:      serverTimestamp(),
          updatedBy:      currentUser.email,
        });
        setEditingId(null);
        return;
      }
      const rates = await getCurrentWeekRates();
      const currency = form.currency || 'GBP';
      const amount = Number(form.amount);
      await updateDoc(doc(db, 'expenses', id), {
        ...form,
        amount,
        gbpEquivalent: toGBP(amount, currency, rates),
        exchangeRateUsed: getRateForCurrency(currency, rates),
        rateWeek: rates.weekStart,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      });
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this expense? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await deleteDoc(doc(db, 'expenses', id));
    } finally {
      setDeletingId(null);
    }
  }

  async function handleToggleStatus(expId, current) {
    const next = (current || 'paid') === 'paid' ? 'to_pay' : 'paid';
    await updateDoc(doc(db, 'expenses', expId), { paymentStatus: next });
  }

  // All MST expenses across all years — used by ExportPanel
  const allMstExpenses = expenses.filter((e) => !e.brand || e.brand === 'mst');

  // Filter to MST + year
  const yearExpenses = allMstExpenses.filter((e) => {
    const d = parseLocalDate(e.date);
    return d && d.getFullYear() === periodYear;
  });

  // When a date range is set, expand scope across all years; otherwise stay within the selected year
  const useDateRange = !!(filterDateFrom || filterDateTo);
  const tableBase = useDateRange ? allMstExpenses : yearExpenses;

  const filtered = tableBase.filter((e) => {
    if (filterTour !== 'all') {
      if (filterTour === 'misc' ? e.requestId !== 'misc' : e.requestId !== filterTour) return false;
    }
    if (filterDateFrom && e.date < filterDateFrom) return false;
    if (filterDateTo   && e.date > filterDateTo)   return false;
    if (filterCategory !== 'all' && e.category !== filterCategory) return false;
    if (filterCurrency !== 'all' && (e.currency || 'GBP') !== filterCurrency) return false;
    if (filterStatus   !== 'all' && (e.paymentStatus || 'paid') !== filterStatus) return false;
    return true;
  });

  const anyFilterActive = filterTour !== 'all' || filterDateFrom || filterDateTo
    || filterCategory !== 'all' || filterCurrency !== 'all' || filterStatus !== 'all';

  function clearFilters() {
    setFilterTour('all'); setFilterDateFrom(''); setFilterDateTo('');
    setFilterCategory('all'); setFilterCurrency('all'); setFilterStatus('all');
  }

  // Invoices kept on file with no figures stay out of every total and count
  const yearCounted = yearExpenses.filter((e) => !e.storedOnly);

  // Category totals keyed as "Category__CURRENCY" to preserve per-currency amounts
  const totalByCategory = yearCounted.reduce((acc, e) => {
    const key = `${e.category || 'Other'}__${e.currency || 'GBP'}`;
    acc[key] = (acc[key] || 0) + (Number(e.amount) || 0);
    return acc;
  }, {});

  // Total per currency for the headline card
  const totalByCurrency = yearCounted.reduce((acc, e) => {
    const c = e.currency || 'GBP';
    acc[c] = (acc[c] || 0) + (Number(e.amount) || 0);
    return acc;
  }, {});

  // GBP equivalent total — uses locked rate on each expense, falls back to amount for legacy
  const totalGBP = yearCounted.reduce((sum, e) => sum + (Number(e.gbpEquivalent ?? e.amount) || 0), 0);
  const hasNonGBP = yearCounted.some((e) => (e.currency || 'GBP') !== 'GBP');

  const usedCategories = [...new Set(yearExpenses.map((e) => e.category || 'Other'))];
  const usedCurrencies = [...new Set(yearCounted.map((e) => e.currency || 'GBP'))];

  const tourOptions = [...new Map(
    allMstExpenses
      .filter((e) => e.requestId && e.requestId !== 'misc')
      .map((e) => {
        const req   = requests.find((r) => r.id === e.requestId);
        const label = req
          ? (req.internalRef || `${req.sport || '?'} — ${req.destination || '?'}${req.departureDate ? ` (${req.departureDate})` : ''}`)
          : (e.requestSummary || e.requestId);
        return [e.requestId, label];
      })
  ).entries()].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
  const hasMiscExpenses = allMstExpenses.some((e) => e.requestId === 'misc');

  return (
    <>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-[20px] font-bold text-[#111827]">Expenses</h2>
          <p className="text-[13px] text-[#6B7280] mt-1">MST Sports Tours costs and overheads</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <button onClick={() => navigate('/staff/financials')}
            className="text-[13px] text-pb hover:text-navy font-medium border border-[#E5E7EB] hover:border-pb/40 px-4 py-2 rounded-[8px] transition">
            ← Financial Dashboard
          </button>
          {!showForm && !scanMode && !showExport && !showInvoiceUpload && (
            <>
              <button
                onClick={() => setShowExport(true)}
                className="flex items-center gap-1.5 border border-[#E5E7EB] hover:border-pb/40 text-[#374151] hover:text-pb text-[13px] font-semibold px-[18px] py-2 rounded-[8px] transition"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export
              </button>
              <button
                onClick={() => { setScanMode(true); setShowForm(false); }}
                className="flex items-center gap-1.5 border border-[#E5E7EB] hover:border-pb/40 text-[#374151] hover:text-pb text-[13px] font-semibold px-[18px] py-2 rounded-[8px] transition"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Scan Receipt
              </button>
              <button
                onClick={() => setShowInvoiceUpload(true)}
                className="flex items-center gap-1.5 border border-[#E5E7EB] hover:border-pb/40 text-[#374151] hover:text-pb text-[13px] font-semibold px-[18px] py-2 rounded-[8px] transition"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Upload Invoice
              </button>
              <button
                onClick={() => { setShowForm(true); setFormInitial(null); setReceiptPreview(null); }}
                className="bg-pb hover:bg-navy text-white text-[13px] font-semibold px-[18px] py-2 rounded-[8px] transition"
              >
                + Add Expense
              </button>
            </>
          )}
        </div>
      </div>

      {/* Scan panel — QR code on desktop, direct camera on mobile */}
      {scanMode && (
        isMobile
          ? <MobileDirectScanPanel
              currentUser={currentUser}
              onScanned={handleScanned}
              onCancel={() => setScanMode(false)}
            />
          : <QRScanPanel
              currentUser={currentUser}
              onScanned={handleScanned}
              onCancel={() => setScanMode(false)}
            />
      )}

      {/* Invoice upload */}
      {showInvoiceUpload && (
        <div className="mb-6">
          <InvoiceUpload
            requests={requests}
            extraFields={{ brand: 'mst' }}
            onSaved={(form) => setPeriodYear(parseLocalDate(form.date).getFullYear())}
            onClose={() => setShowInvoiceUpload(false)}
          />
        </div>
      )}

      {/* Export panel */}
      {showExport && (
        <ExportPanel
          allExpenses={allMstExpenses}
          requests={requests}
          onClose={() => setShowExport(false)}
        />
      )}

      {/* Add / pre-filled form */}
      {showForm && (
        <div className="mb-6">
          <ExpenseForm
            initial={formInitial || EMPTY_FORM}
            requests={requests}
            onSave={handleSave}
            onCancel={cancelForm}
            saving={saving}
            saveError={saveError}
            receiptPreview={receiptPreview}
          />
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-[10px] border border-[#E5E7EB] px-5 py-4 col-span-2 sm:col-span-1">
          <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-2">Total {periodYear}</p>
          <p className="text-[18px] font-bold text-[#111827] leading-tight">
            {Object.entries(totalByCurrency).filter(([, v]) => v > 0).map(([c, v]) =>
              `${CURRENCY_SYMBOLS[c] || '£'}${v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            ).join(' + ') || '—'}
          </p>
          <p className="text-[11px] text-[#9CA3AF] mt-0.5">{yearCounted.length} expense{yearCounted.length !== 1 ? 's' : ''}</p>
        </div>
        {hasNonGBP && (
          <div className="bg-white rounded-[10px] border border-[#E5E7EB] px-5 py-4 col-span-2 sm:col-span-1">
            <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-2">GBP Equivalent {periodYear}</p>
            <p className="text-[18px] font-bold text-[#111827] leading-tight">{fmt(totalGBP, 'GBP')}</p>
            <p className="text-[11px] text-[#9CA3AF] mt-0.5">All currencies converted</p>
          </div>
        )}
        {Object.entries(totalByCategory)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([key, total]) => {
            const [cat, cur] = key.split('__');
            return (
              <div key={key} className="bg-white rounded-[10px] border border-[#E5E7EB] px-5 py-4">
                <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-2 truncate">{cat}</p>
                <p className="text-[20px] font-bold text-[#111827]">{fmt(total, cur)}</p>
              </div>
            );
          })}
      </div>

      {/* Filters + Table */}
      <div className="bg-white rounded-[12px] border border-[#E5E7EB] overflow-hidden">
        <div className="px-5 pt-3 pb-3 border-b border-[#E5E7EB] space-y-3">

          {/* Row 1: Year nav + dropdown/input filters */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Year nav */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button onClick={() => setPeriodYear((y) => y - 1)}
                className="w-7 h-7 flex items-center justify-center rounded-full text-[#6B7280] hover:bg-surface transition">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-[14px] font-bold text-[#111827] min-w-[46px] text-center">{periodYear}</span>
              <button onClick={() => setPeriodYear((y) => y + 1)}
                className="w-7 h-7 flex items-center justify-center rounded-full text-[#6B7280] hover:bg-surface transition">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <div className="h-4 w-px bg-[#E5E7EB] flex-shrink-0" />

            {/* Tour */}
            <select value={filterTour} onChange={(e) => setFilterTour(e.target.value)}
              className="border border-[#E5E7EB] rounded-[8px] px-3 py-1.5 text-[12px] text-[#374151] focus:outline-none focus:ring-2 focus:ring-pb bg-white">
              <option value="all">All tours</option>
              {hasMiscExpenses && <option value="misc">MISC</option>}
              {tourOptions.map(({ id, label }) => (
                <option key={id} value={id}>{label}</option>
              ))}
            </select>

            {/* Date from */}
            <div className="flex items-center gap-1.5">
              <label className="text-[11px] font-medium text-[#9CA3AF] flex-shrink-0">From</label>
              <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)}
                className="border border-[#E5E7EB] rounded-[8px] px-3 py-1.5 text-[12px] text-[#374151] focus:outline-none focus:ring-2 focus:ring-pb" />
            </div>

            {/* Date to */}
            <div className="flex items-center gap-1.5">
              <label className="text-[11px] font-medium text-[#9CA3AF] flex-shrink-0">To</label>
              <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)}
                className="border border-[#E5E7EB] rounded-[8px] px-3 py-1.5 text-[12px] text-[#374151] focus:outline-none focus:ring-2 focus:ring-pb" />
            </div>

            {/* Status */}
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-[#E5E7EB] rounded-[8px] px-3 py-1.5 text-[12px] text-[#374151] focus:outline-none focus:ring-2 focus:ring-pb bg-white">
              <option value="all">All statuses</option>
              <option value="paid">Paid</option>
              <option value="to_pay">To Pay</option>
              <option value="stored">Stored</option>
            </select>

            {/* Clear all */}
            {anyFilterActive && (
              <button onClick={clearFilters}
                className="flex items-center gap-1 text-[12px] text-[#9CA3AF] hover:text-dr transition ml-auto flex-shrink-0">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear filters
              </button>
            )}
          </div>

          {/* Row 2: Category + Currency chips */}
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setFilterCategory('all')}
              className={`px-3 py-1 rounded-full text-[12px] font-semibold transition ${
                filterCategory === 'all' ? 'bg-pb text-white' : 'bg-surface text-[#6B7280] hover:text-[#374151]'
              }`}>
              All
            </button>
            {usedCategories.map((cat) => (
              <button key={cat} onClick={() => setFilterCategory(cat)}
                className={`px-3 py-1 rounded-full text-[12px] font-semibold transition ${
                  filterCategory === cat ? 'bg-pb text-white' : 'bg-surface text-[#6B7280] hover:text-[#374151]'
                }`}>
                {cat}
              </button>
            ))}

            {usedCurrencies.length > 1 && (
              <>
                <div className="h-4 w-px bg-[#E5E7EB]" />
                {['all', ...usedCurrencies].map((c) => (
                  <button key={c} onClick={() => setFilterCurrency(c)}
                    className={`px-3 py-1 rounded-full text-[12px] font-semibold transition ${
                      filterCurrency === c ? 'bg-navy text-white' : 'bg-surface text-[#6B7280] hover:text-[#374151]'
                    }`}>
                    {c === 'all' ? 'All currencies' : c}
                  </button>
                ))}
              </>
            )}
          </div>

          {/* Cross-year date range notice */}
          {useDateRange && (
            <p className="text-[11px] text-[#9CA3AF]">
              Showing results across all years — year selector above applies to summary cards only.
            </p>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div className="py-12 text-center text-[13px] text-[#9CA3AF]">Loading expenses…</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-[13px] text-[#9CA3AF] mb-2">No expenses recorded for {periodYear}.</p>
            {!showForm && !scanMode && (
              <div className="flex items-center justify-center gap-3 mt-3">
                <button onClick={() => setScanMode(true)}
                  className="text-[13px] text-pb hover:text-navy font-medium">
                  Scan a receipt
                </button>
                <span className="text-[#D1D5DB]">·</span>
                <button onClick={() => { setShowForm(true); setFormInitial(null); }}
                  className="text-[13px] text-pb hover:text-navy font-medium">
                  Add manually
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[620px]">
            <thead className="bg-surface border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left px-5 py-3 text-[11px] font-bold text-[#6B7280] uppercase">Date</th>
                <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase">Category</th>
                <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase">Description</th>
                <th className="text-left px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase">Linked Tour</th>
                <th className="text-right px-5 py-3 text-[11px] font-bold text-[#6B7280] uppercase">Amount</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F4F6]">
              {filtered.map((exp) => (
                editingId === exp.id ? (
                  <tr key={exp.id}>
                    <td colSpan={6} className="px-5 py-3">
                      <ExpenseForm
                        initial={{
                          date:           exp.date || '',
                          category:       exp.category || 'Other',
                          description:    exp.description || '',
                          amount:         String(exp.amount || ''),
                          currency:       exp.currency || 'GBP',
                          requestId:      exp.requestId || '',
                          requestSummary: exp.requestSummary || '',
                          receiptUrl:     exp.receiptUrl || '',
                          paymentStatus:  exp.paymentStatus || 'paid',
                          storedOnly:     !!exp.storedOnly,
                        }}
                        requests={requests}
                        onSave={(form) => handleEdit(exp.id, form)}
                        onCancel={() => setEditingId(null)}
                        saving={saving}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr key={exp.id} className="hover:bg-surface transition">
                    <td className="px-5 py-3 text-[12px] text-[#374151] whitespace-nowrap">{fmtDate(exp.date)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#F3F4F6] text-[11px] font-medium text-[#374151]">
                        {exp.category || 'Other'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-[#374151]">
                      <div className="flex items-center gap-2">
                        {exp.description || '—'}
                        {exp.receiptUrl && (
                          <a
                            href={exp.receiptUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={exp.invoiceFileName ? 'View invoice' : 'View receipt'}
                            className="flex-shrink-0 text-[#9CA3AF] hover:text-pb transition"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[12px] text-[#6B7280]">
                      {exp.requestSummary === 'MISC' || exp.requestId === 'misc' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#F3F4F6] text-[11px] font-medium text-[#6B7280]">MISC</span>
                      ) : exp.requestId ? (() => {
                        const req = requests.find((r) => r.id === exp.requestId);
                        const primary = req?.internalRef || exp.requestSummary || exp.requestId;
                        const sub = req?.internalRef && exp.requestSummary && exp.requestSummary !== req.internalRef ? exp.requestSummary : null;
                        return (
                          <button
                            onClick={() => navigate(`/staff/requests/${exp.requestId}`)}
                            className="hover:text-pb hover:underline transition text-left"
                          >
                            <p className="text-[12px] font-semibold text-[#374151]">{primary}</p>
                            {sub && <p className="text-[10.5px] text-[#9CA3AF]">{sub}</p>}
                          </button>
                        );
                      })() : '—'}
                    </td>
                    <td className="px-5 py-3 text-right font-semibold text-[13px] text-[#111827] whitespace-nowrap">
                      {exp.storedOnly ? <span className="font-normal text-[#9CA3AF]">—</span> : fmt(exp.amount, exp.currency || 'GBP')}
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-3">
                        {exp.storedOnly ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#F3F4F6] text-[#6B7280]">
                            Stored
                          </span>
                        ) : (
                          <button
                            onClick={() => handleToggleStatus(exp.id, exp.paymentStatus)}
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold transition ${
                              (exp.paymentStatus || 'paid') === 'paid'
                                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                            }`}
                          >
                            {(exp.paymentStatus || 'paid') === 'paid' ? 'Paid' : 'To Pay'}
                          </button>
                        )}
                        <button
                          onClick={() => setEditingId(exp.id)}
                          className="text-[12px] text-pb hover:text-navy font-medium">
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(exp.id)}
                          disabled={deletingId === exp.id}
                          className="text-[12px] text-dr/70 hover:text-dr font-medium disabled:opacity-50">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              ))}
            </tbody>

            {/* Footer totals */}
            {filtered.length > 0 && (
              <tfoot className="bg-[#FAFAFA] border-t border-[#E5E7EB]">
                {Object.entries(
                  filtered.filter((e) => !e.storedOnly).reduce((acc, e) => {
                    const c = e.currency || 'GBP';
                    acc[c] = (acc[c] || 0) + (Number(e.amount) || 0);
                    return acc;
                  }, {})
                ).map(([currency, total]) => (
                  <tr key={currency}>
                    <td colSpan={4} className="px-5 py-2.5 text-[12px] font-bold text-[#6B7280] uppercase tracking-wide">
                      Total ({currency}) — {filtered.filter((e) => !e.storedOnly && (e.currency || 'GBP') === currency).length} expenses
                    </td>
                    <td className="px-5 py-2.5 text-right text-[14px] font-bold text-[#111827]">{fmt(total, currency)}</td>
                    <td className="px-5 py-2.5" />
                  </tr>
                ))}
              </tfoot>
            )}
          </table>
          </div>
        )}
      </div>
    </>
  );
}
